import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './config';
import { writeCanvasState, writeCursor, removeCursor } from './session';
import { useSessionStore } from '../store/sessionStore';
import { useCursorStore } from '../store/cursorStore';
import { SessionState, PMNode, Cursor, canvasNodeToProcessNode } from '../types';

// ── Debounce util ─────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
    let timer: ReturnType<typeof setTimeout>;
    return ((...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), ms);
    }) as T;
}

// ── Estado interno ────────────────────────────────────────────────────────────

let currentSessionId: string | null = null;
let currentUid: string | null = null;
let currentCursorId: string | null = null;
let unsubscribe: (() => void) | null = null;

// Flag para ignorar el snapshot que generamos nosotros mismos
let localWriteInFlight = false;

// ── Write debounceado (100ms para movimientos, inmediato para cambios de estado) ──

const debouncedWrite = debounce(async (sessionId: string, state: SessionState) => {
    localWriteInFlight = true;
    try {
        await writeCanvasState(sessionId, state);
    } finally {
        // Deja pasar el siguiente snapshot antes de bajar el flag
        setTimeout(() => { localWriteInFlight = false; }, 200);
    }
}, 100);

// ── Iniciar sincronización ────────────────────────────────────────────────────

export function startSync(sessionId: string, uid: string, cursorId: string): void {
    if (unsubscribe) unsubscribe();
    currentSessionId = sessionId;
    currentUid = uid;
    currentCursorId = cursorId;

    // Suscribirse a cambios del documento de sesión
    unsubscribe = onSnapshot(
        doc(db, 'sessions', sessionId),
        (snap) => {
            if (!snap.exists()) return;

            // Ignorar el echo de nuestras propias escrituras
            if (localWriteInFlight) return;

            const data = snap.data();

            // ── Sincronizar cursores ──
            const cursors: Record<string, Cursor> = data.cursors ?? {};
            const cursorStore = useCursorStore.getState();
            // Limpiar cursores que ya no existen
            Object.keys(useCursorStore.getState().cursors).forEach(id => {
                if (!cursors[id]) cursorStore.removeCursor(id);
            });
            // Actualizar/añadir cursores activos (excluyendo el propio)
            Object.entries(cursors).forEach(([id, raw]) => {
                const c = raw as Cursor;
                if (id === cursorId || c.cursorId === cursorId) return;
                // Ignorar cursores inactivos > 10 segundos
                if (Date.now() - (c.updatedAt ?? 0) > 10_000) {
                    cursorStore.removeCursor(id);
                    return;
                }
                cursorStore.setCursor(id, { ...c, cursorId: c.cursorId ?? id });
            });

            // ── Sincronizar estado del lienzo ──
            const nodes: Record<string, PMNode> = data.nodes ?? {};
            const remoteState: SessionState = {
                version: data.version ?? 1,
                nodes: Object.values(nodes).map(canvasNodeToProcessNode),
                layout: {
                    nodes: Object.values(nodes),
                    // El viewBox siempre es local por usuario: no se sincroniza.
                    viewBox: useSessionStore.getState().viewBox,
                },
                processes: data.processes ?? [],
                edges: data.edges ?? [],
                people: data.people ?? [],
            };

            useSessionStore.getState().loadState(remoteState);
        },
        (error) => {
            console.error('[sync] onSnapshot error:', error);
        },
    );
}

// ── Parar sincronización ──────────────────────────────────────────────────────

export function stopSync(): void {
    if (unsubscribe) { unsubscribe(); unsubscribe = null; }
    if (currentSessionId && currentCursorId) {
        removeCursor(currentSessionId, currentCursorId).catch(() => { });
    }
    currentSessionId = null;
    currentUid = null;
    currentCursorId = null;
    useCursorStore.getState().clearCursors();
}

// ── Publicar cambio local → Firestore ────────────────────────────────────────
// Llama esto desde los hooks cada vez que el store cambie

export function pushCanvasState(): void {
    if (!currentSessionId) return;
    const state = useSessionStore.getState().getState();
    debouncedWrite(currentSessionId, state);
}

// ── Publicar cursor propio (debounce 50ms) ────────────────────────────────────

const debouncedCursor = debounce(async (
    sessionId: string, cursorId: string, uid: string,
    x: number, y: number, displayName: string, color: string,
) => {
    await writeCursor(sessionId, cursorId, uid, x, y, displayName, color);
}, 50);

export function pushCursor(x: number, y: number, displayName: string, color: string): void {
    if (!currentSessionId || !currentUid || !currentCursorId) return;
    debouncedCursor(currentSessionId, currentCursorId, currentUid, x, y, displayName, color);
}
